import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChildren,
} from '@angular/core';
import { videoLayout2Style } from '@components/video/components/video-layout-2/config';
import { VideoLayout2, VideoLayout2Style } from '@components/video/components/video-layout-2/model';
import { AnchorService } from '@services/anchor';
import { ResponsiveService } from '@services/responsive';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { VideoComponent } from '@shared/components/video';
import { Video } from '@shared/components/video/model';
import { ColorPipe } from '@shared/pipes';
import { getId } from '@shared/utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-video-layout-2',
  imports: [TitleComponent, VideoComponent, ColorPipe],
  providers: [AnchorService],
  templateUrl: './video-layout-2.component.html',
  styleUrl: './video-layout-2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoLayout2Component implements OnInit, OnDestroy {
  readonly data = input.required<VideoLayout2>();
  readonly videoElements = viewChildren('videoElements', { read: ElementRef });
  activeItem = signal(0);
  title = signal<Title>(null!);
  items = signal<{ title: Title; text: string; video: Video }[]>(null!);
  style = signal<VideoLayout2Style>(null!);
  readonly idComponent = `video-layout-2-${getId()}`;
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  readonly #document = inject<Document>(DOCUMENT);
  readonly #window = this.#document.defaultView as Window;
  readonly #anchorService = inject(AnchorService);
  readonly #offsetGap = 520;
  #destroy$: Subject<boolean> = new Subject<boolean>();

  constructor() {
    afterNextRender(() => {
      void this.#anchorService
        .monitorUrlAnchor('capsules-deco', this.idComponent)
        .pipe(takeUntil(this.#destroy$))
        .subscribe();
    });
  }

  ngOnInit(): void {
    this.style.set(videoLayout2Style[this.data().theme ?? 'BLUE']!);
    this.title.set({
      level: 'H2',
      type: 'SLASH',
      color: this.style().labelColor,
      text: this.data().title,
      align: {
        mobile: 'LEFT',
        desktop: 'LEFT',
      },
      weight: 500,
      size: {
        mobile: 'SPAN',
        desktop: 'SPAN',
      },
    });
    this.items.set(
      this.data().items.map((item) => ({
        ...item,
        title: {
          level: 'SPAN',
          size: {
            mobile: 'H3',
            desktop: 'H3',
          },
          color: this.style().textColor,
          align: {
            mobile: 'LEFT',
            desktop: 'LEFT',
          },
          text: item.title,
        },
      }))
    );
  }

  @HostListener('window:scroll') onScrollEvent() {
    const videoElements = this.videoElements();
    if (videoElements && videoElements.length && this.breakpoints() === 'desktop') {
      videoElements.forEach((videoElement, i) => {
        const el = videoElement.nativeElement as HTMLElement;
        if (
          this.#window.scrollY >= el.offsetTop - this.#offsetGap &&
          this.#window.scrollY <= el.offsetTop + el.offsetHeight
        ) {
          this.activeItem.set(i);
        }
      });
    }
  }

  goToSection(anchor: string) {
    this.#document.getElementById(anchor)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }

  ngOnDestroy(): void {
    this.#destroy$.next(true);
    this.#destroy$.complete();
  }
}
